/**
 * `POST /internal/cron/rated-rollup-fold`.
 *
 * Folds ONE-HOUR slices (Shanghai hours) of `usage_rated_events` into the rated day rollups
 * (`usage_rated_daily_rollup`, `usage_rated_model_daily_rollup`, migration 0120). A day whose 24
 * slices are all folded is compacted into one whole-day row set (`hour = 24`) and marked in
 * `usage_rated_daily_rollup_days`; only marked days are ever served. Control-loops ticks it hourly,
 * and only while `RATED_ROLLUP_FOLD_ARMED` is "1": unarmed, nothing is requested, read or written.
 *
 * The cost of a slice depends on one hour of events, never on a whole day or on the window, and every
 * slice runs under the platform's usual statement budget (the same 8 s as the funding rollup). A slice
 * that times out is parked with a backoff and does not hold up the slices behind it.
 *
 * STALENESS (the contract of the readers that use these tables: ops throughput series, email notice
 * audience; NOT the pricing anchor, which re-checks the hot table at read time): a served day can lag the
 * hot table by at most one fold interval plus the watermark margin for a verdict the fold can see (a verdict
 * rated after a slice was folded marks that slice; the slice is un-served until it is folded again), and by
 * at most `lookback x 24` fold ticks for a verdict the watermark could not see (a transaction committed
 * more than the margin after its `rated_at`), which the per-tick audit re-folds oldest first. A reader
 * ignores the rollups altogether when the fold has not completed a tick in the last
 * {@link RATED_ROLLUP_READ_MAX_AGE_MS}, so a paused or disarmed fold sends the readers back to the tables.
 *
 * cron_run_history is written by control-loops' tick-level recordCronRun.
 */

/** A slice is folded once its end is this old. */
export const RATED_ROLLUP_SEAL_GRACE_MS = 30 * 60 * 1000;
/** A reader ignores the rollups when the fold's state row is older than this (3 missed hourly ticks). */
export const RATED_ROLLUP_READ_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/** Slices one call may fold (late-marked first, then missing slices newest first, then the audit). */
export const RATED_ROLLUP_FOLD_MAX_HOURS_PER_CALL = 6;
export const RATED_ROLLUP_FOLD_MAX_HOURS_CEILING = 24;
/** Folded slices re-folded per call to catch a verdict the watermark could not see (oldest fold first). */
export const RATED_ROLLUP_FOLD_AUDIT_HOURS_PER_CALL = 1;
export const RATED_ROLLUP_FOLD_AUDIT_HOURS_CEILING = 4;
/** The same statement budget and wall as `revenue_funding_materialize`; no preset raises them. */
export const RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MS = 8_000;
export const RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MIN_MS = 2_000;
export const RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MAX_MS = RATED_ROLLUP_FOLD_STATEMENT_TIMEOUT_MS;
/** Route wall-clock budget. The control-loops fetch/handler timeouts sit above this. */
export const RATED_ROLLUP_FOLD_WALL_MS = 25_000;
export const RATED_ROLLUP_FOLD_WALL_MIN_MS = 10_000;
export const RATED_ROLLUP_FOLD_WALL_MAX_MS = RATED_ROLLUP_FOLD_WALL_MS;

export interface RatedRollupFoldCronRequest {
  /** Slices one call may fold. */
  readonly maxHours?: number;
  /** Folded slices re-folded for the audit. */
  readonly auditHours?: number;
  /** Per-statement budget; at most the default. */
  readonly statementTimeoutMs?: number;
  /** Route wall budget; at most the default. */
  readonly wallMs?: number;
}

export interface RatedRollupFoldCronResponse {
  readonly ok: boolean;
  /** Set when the platform is not armed: nothing was read or written. */
  readonly skipped?: 'off';
  readonly foldedHours: number;
  /** Slices that were folded before and folded again (a late verdict, or the audit). */
  readonly refoldedHours: number;
  /** Days that became whole and were compacted/marked this call. */
  readonly sealedDays: number;
  /** Sealed slices still without a rollup after this call (not parked). */
  readonly remaining_hours: number;
  /** Slices this call lost to statement_timeout (SQLSTATE 57014); they are parked. */
  readonly timeout_hours: number;
  /** Slices waiting out a backoff after an earlier timeout. */
  readonly parked_hours: number;
  /** Slices that cannot be folded from the hot table (reaped, resident cold, or past the horizon). */
  readonly unavailable_hours: number;
  readonly errors: readonly string[];
}
